import { useMemo } from "react";
import { CATEGORIES, CATEGORY_MAP } from "../utils/constants";
import { getRelativeDate } from "../utils/helpers";
import ProfileAvatar from "./ProfileAvatar";
import styles from "../styles/theme";

export default function PeopleListView({
  people,
  searchQuery,
  setSearchQuery,
  filterCategory,
  setFilterCategory,
  onSelect,
  onEdit,
  onDelete,
}) {
  const filtered = useMemo(() => {
    let list = [...people];
    if (filterCategory !== "all")
      list = list.filter((p) => p.category === filterCategory);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.relationship?.toLowerCase().includes(q) ||
          p.meetingPlace?.toLowerCase().includes(q) ||
          p.notes?.toLowerCase().includes(q) ||
          p.phoneNumber?.toLowerCase().includes(q) ||
          p.jobDescription?.toLowerCase().includes(q),
      );
    }
    return list.sort((a, b) => new Date(b.dateAdded) - new Date(a.dateAdded));
  }, [people, filterCategory, searchQuery]);

  return (
    <div style={styles.peopleList}>
      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.searchWrapper}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            style={styles.searchInput}
            placeholder="Search people..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div style={styles.categoryFilter}>
          <button
            onClick={() => setFilterCategory("all")}
            style={{
              ...styles.filterChip,
              ...(filterCategory === "all" ? styles.filterChipActive : {}),
            }}
          >
            All ({people.length})
          </button>
          {CATEGORIES.map((c) => {
            const count = people.filter((p) => p.category === c.id).length;
            if (count === 0) return null;
            return (
              <button
                key={c.id}
                onClick={() => setFilterCategory(c.id)}
                style={{
                  ...styles.filterChip,
                  ...(filterCategory === c.id
                    ? { ...styles.filterChipActive, background: c.color }
                    : {}),
                }}
              >
                {c.icon} {c.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={{ ...styles.emptyState, padding: 60 }}>
          <p style={styles.emptyText}>
            {searchQuery ? "No results found." : "No people in this category."}
          </p>
        </div>
      ) : (
        <div style={styles.peopleGrid}>
          {filtered.map((person) => {
            const cat = CATEGORY_MAP[person.category];
            return (
              <div
                key={person.id}
                style={styles.personCard}
                onClick={() => onSelect(person)}
              >
                <div style={styles.personCardHeader}>
                  <ProfileAvatar
                    person={person}
                    size={40}
                    fontSize={16}
                    color={cat?.color}
                  />
                  <div>
                    <div style={styles.personCardName}>{person.name}</div>
                    <div style={styles.personCardRelation}>
                      {person.relationship}
                    </div>
                  </div>
                </div>
                <div style={styles.personCardMeta}>
                  <span
                    style={{
                      ...styles.personCardCategory,
                      borderColor: cat?.color,
                    }}
                  >
                    {cat?.icon} {cat?.label}
                  </span>
                  <span style={styles.personCardDate}>
                    {getRelativeDate(person.dateAdded)}
                  </span>
                </div>
                {person.jobDescription && (
                  <div style={styles.personCardDetail}>
                    💼 {person.jobDescription}
                  </div>
                )}
                {person.phoneNumber && (
                  <div style={styles.personCardDetail}>
                    📞 {person.phoneNumber}
                    {person.phoneNumberGiven
                      ? ` · ${person.phoneNumberGiven === "primary_a" ? "Primary (A)" : "Secondary (M)"}`
                      : ""}
                  </div>
                )}
                {person.meetingPlace && (
                  <div style={styles.personCardDetail}>
                    📍 {person.meetingPlace}
                  </div>
                )}
                {person.dateMet && (
                  <div style={styles.personCardDetail}>
                    🤝 Met{" "}
                    {new Date(person.dateMet + "T00:00:00").toLocaleDateString(
                      "en-US",
                      { month: "short", day: "numeric", year: "numeric" },
                    )}
                  </div>
                )}
                {person.birthdate && (
                  <div style={styles.personCardDetail}>
                    🎂 Born{" "}
                    {new Date(
                      person.birthdate + "T00:00:00",
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                )}
                {person.intimatePartner && (
                  <div style={styles.personCardDetail}>
                    💜 Intimate Partner
                    {person.intimateStart || person.intimateEnd
                      ? ` · ${person.intimateStart ? new Date(person.intimateStart + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "?"} – ${person.intimateEnd ? new Date(person.intimateEnd + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "Present"}`
                      : ""}
                  </div>
                )}
                {person.nightsTogether && person.nightsTogether.length > 0 && (
                  <div style={styles.personCardDetail}>
                    🌙 {person.nightsTogether.length} Night
                    {person.nightsTogether.length !== 1 ? "s" : ""} Together
                  </div>
                )}
                {person.notes && (
                  <div style={styles.personCardNotes}>
                    {person.notes.length > 80
                      ? person.notes.slice(0, 80) + "…"
                      : person.notes}
                  </div>
                )}
                <div style={styles.personCardActions}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(person);
                    }}
                    style={styles.cardActionBtn}
                  >
                    Edit
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (confirm(`Remove ${person.name}?`))
                        onDelete(person.id);
                    }}
                    style={{ ...styles.cardActionBtn, color: "#e06060" }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── PERSON FORM MODAL ───────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
